import type { NoteAsset } from '../assets/assetTypes'
import { APP_SETTINGS } from '../config/appSettings'

const STAGE = APP_SETTINGS.images.stage

export type ImageStageLook = 'large' | 'compact'

/**
 * What a person wrote, in characters. A link is an item and a #tag is a label, so neither counts, nor do the marks that make a
 * heading, a list or a checkbox. Only the weight of the prose matters here, not its exact length.
 */
export function proseLength(body: string): number {
  return body
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/(^|\s)#[\p{L}\p{N}_-]+/gu, ' ')
    .replace(/^[ \t]*(?:-[ \t]\[[ xX]\]|[#>*+-]+|\d+[.)])[ \t]+/gm, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim().length
}

/** A note is text-led when its prose is longer than the setting; below that the pictures are what the note is about. */
export function isTextLed(body: string): boolean {
  return proseLength(body) > STAGE.textLedMinChars
}

/** width ÷ height of an image; an image whose size is not known yet is treated as a common 4:3 photo until it is. */
export function assetAspect(asset: Pick<NoteAsset, 'width' | 'height'>): number {
  return asset.width && asset.height ? asset.width / asset.height : 4 / 3
}

/** Taller than a 5:3 portrait: a page, a long screenshot. It is shown as wide as it can be and cropped by height, not shrunk to fit. */
export function isTallAspect(aspect: number): boolean {
  return aspect < STAGE.tallAspect
}

export interface StageTiles<T> {
  shown: T[]
  /** Pictures beyond the tiles that fit, drawn as one "+N" tile over the next picture. 0 when everything fits. */
  more: number
}

/**
 * Everything is shown while it fits; past `maxTiles` the last cell becomes "+N" over the first picture that did not fit, so a tap
 * on it opens the viewer there. No picture is promoted: the first ones simply come first, in the order they were added.
 */
export function stageTiles<T>(items: readonly T[]): StageTiles<T> {
  if (items.length <= STAGE.maxTiles) return { shown: [...items], more: 0 }
  const fit = STAGE.maxTiles - 1
  return { shown: items.slice(0, fit + 1), more: items.length - fit }
}
